/**
 * Editor de rateio: divide o valor de um lançamento entre duas ou mais
 * categorias do plano de contas. Usado pelo modal de lançamento (conta
 * corrente e fatura de cartão) e pela conciliação Itaú.
 *
 * A conta é feita em centavos (allocationCalc.ts); este componente só
 * desenha as linhas e mostra o que falta/sobra — a validação bloqueante fica
 * no submit de quem o usa.
 */
import React from 'react';
import { Plus, Trash2, Wand2 } from 'lucide-react';
import { clsx } from 'clsx';
import type { CategoryOptionGroup } from './financialUtils';
import { formatCurrencyBRL } from './financialUtils';
import { CategorySelect, inputCls } from './financialShared';
import {
  allocationsTotalCents,
  allocationsValidationMessage,
  distributeRemaining,
  newAllocationLine,
  remainingCents,
  type AllocationLine,
} from './allocationCalc';

interface AllocationEditorProps {
  /** Valor do lançamento em centavos (sempre positivo). */
  totalCents: number;
  /** Grupos já filtrados pelo sentido do lançamento (Saída/Entrada). */
  categoryGroups: CategoryOptionGroup[];
  lines: AllocationLine[];
  onChange: (lines: AllocationLine[]) => void;
  disabled?: boolean;
}

const brl = (cents: number): string => formatCurrencyBRL(cents / 100);

export const AllocationEditor: React.FC<AllocationEditorProps> = ({ totalCents, categoryGroups, lines, onChange, disabled }) => {
  const rateado = allocationsTotalCents(lines);
  const restante = remainingCents(totalCents, lines);
  const message = allocationsValidationMessage(totalCents, lines);

  const update = (tempId: string, patch: Partial<AllocationLine>) =>
    onChange(lines.map((l) => (l.tempId === tempId ? { ...l, ...patch } : l)));

  return (
    <div className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-3 space-y-2">
      <div className="hidden sm:grid grid-cols-[1fr_120px_1fr_32px] gap-2 text-xs font-medium text-slate-500 px-1">
        <span>Categoria</span>
        <span className="text-right">Valor (R$)</span>
        <span>Observação</span>
        <span />
      </div>
      {lines.map((line, index) => (
        <div key={line.tempId} className="grid grid-cols-1 sm:grid-cols-[1fr_120px_1fr_32px] gap-2 items-center">
          <CategorySelect
            groups={categoryGroups}
            value={line.accountingPlanErpId}
            onChange={(erpId) => update(line.tempId, { accountingPlanErpId: erpId })}
            disabled={disabled}
            emptyLabel={`— Categoria da parte ${index + 1} —`}
          />
          <input
            className={clsx(inputCls, 'text-right')}
            inputMode="decimal"
            placeholder="0,00"
            value={line.amountInput}
            disabled={disabled}
            onChange={(e) => update(line.tempId, { amountInput: e.target.value })}
          />
          <input
            className={inputCls}
            placeholder="Observação (ex.: Pedido 3421)"
            maxLength={100}
            value={line.note}
            disabled={disabled}
            onChange={(e) => update(line.tempId, { note: e.target.value })}
          />
          <button
            type="button"
            className="p-1.5 text-slate-400 hover:text-red-500 rounded disabled:opacity-40 justify-self-end"
            title="Remover parte"
            disabled={disabled}
            onClick={() => onChange(lines.filter((l) => l.tempId !== line.tempId))}
          >
            <Trash2 size={15} />
          </button>
        </div>
      ))}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs pt-1">
        <span className="text-slate-500">
          Total do lançamento <strong className="text-slate-800">{brl(totalCents)}</strong>
        </span>
        <span className="text-slate-500">
          Rateado <strong className="text-slate-800">{brl(rateado)}</strong>
        </span>
        <span className={clsx('font-semibold', restante === 0 ? 'text-emerald-700' : 'text-red-600')}>
          Restante {brl(restante)}
        </span>
        <span className="ml-auto flex items-center gap-2">
          {restante > 0 && (
            <button
              type="button"
              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-slate-300 text-slate-600 hover:bg-white disabled:opacity-50"
              disabled={disabled}
              title="Joga o que falta na última parte sem valor"
              onClick={() => onChange(distributeRemaining(totalCents, lines))}
            >
              <Wand2 size={12} /> Distribuir restante
            </button>
          )}
          <button
            type="button"
            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-indigo-300 text-indigo-700 hover:bg-white disabled:opacity-50"
            disabled={disabled}
            onClick={() => onChange([...lines, newAllocationLine()])}
          >
            <Plus size={12} /> Adicionar categoria
          </button>
        </span>
      </div>

      {message && <p className="text-xs text-amber-700">{message}</p>}
    </div>
  );
};
